import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { MoreHorizontal } from 'lucide-react'
import { socialApi } from '@/lib/socialApi'
import { describeError } from '@/lib/auth'
import { activityOf } from '@/lib/presence'
import { openProfile, userContextHandler } from '@/store/contextMenu'
import { useGuildStore } from '@/store/guilds'
import { useSocialStore } from '@/store/social'
import { useCustomStatus, usePresenceStore, useStatusOf } from '@/store/presence'
import { cx } from '@/lib/cx'
import type { User } from '@/types/api'
import { ProfileCard } from '../profile/ProfileCard'
import styles from './DMProfilePanel.module.css'

/**
 * Профиль собеседника справа от личной беседы, как в Discord: баннер, имя, общие друзья и серверы,
 * «о себе», дата в Кличате; внизу «Полный профиль». Та же карточка, что всплывает по нажатию на имя,
 * только без поля «Сообщение для…» — беседа уже открыта.
 */
export function DMProfilePanel({ user }: { user: User }) {
  const profile = useQuery({ queryKey: ['profile', user.id], queryFn: () => socialApi.profile(user.id), staleTime: 60_000 })
  const status = useStatusOf(user.id)
  const activity = useSocialStore((s) => s.activities[user.id])
  const customStatus = useCustomStatus(user.id)
  const activities = usePresenceStore((s) => s.activities[user.id])
  const relationship = useSocialStore((s) => s.relationships[user.id])
  const guilds = useGuildStore((s) => s.guilds)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const canAddFriend = !relationship && !user.bot

  const addFriend = () => {
    setBusy(true)
    setError(null)
    void socialApi.requestFriend(user.username)
      .catch((err: unknown) => setError(describeError(err).message))
      .finally(() => setBusy(false))
  }

  return (
    <aside className={cx(styles.panel, 'scroller')} aria-label="Профиль собеседника">
      <div className={styles.wco} aria-hidden="true" />
      <div className={styles.inner}>
        <div className={styles.card}>
          {/* «…» — то же меню, что по правому клику на имени: заблокировать, пожаловаться, скопировать ID */}
          <button type="button" className={styles.more} data-tooltip="Ещё" aria-label="Ещё" onClick={userContextHandler(user.id, { user })}>
            <MoreHorizontal size={18} />
          </button>
          <ProfileCard
            user={user}
            guilds={guilds}
            profile={profile.data}
            status={status}
            activity={activity}
            customStatus={customStatus}
            activityLine={activityOf(activities)?.label}
            isMe={false}
            canAddFriend={canAddFriend}
            busy={busy}
            error={error}
            onOpenDM={() => undefined}
            onSend={() => undefined}
            onAddFriend={addFriend}
            onOpenMutuals={(tab) => openProfile(user.id, { user, tab })}
            showSend={false}
            inDM
          />
        </div>
        <button type="button" className={styles.full} onClick={() => openProfile(user.id, { user })}>Полный профиль</button>
      </div>
    </aside>
  )
}
